import api from './api';
import {
  Invitation,
  InviteToOrgRequest,
  InviteToBrandSpaceRequest,
  ListInvitationsParams,
  VerifyInvitationResponse,
  AcceptInvitationResponse,
} from '../types/invitations';

/**
 * Invitations Service
 *
 * Connects to backend invitation API endpoints for managing
 * organization and brand space invitations
 */

// ============================================================================
// Service Functions
// ============================================================================

export const invitationsService = {
  // ==========================================================================
  // Organization Invitations
  // ==========================================================================

  /**
   * Invite a user to an organization
   *
   * POST /api/organizations/:orgId/invitations
   *
   * @param orgId - Organization ID
   * @param data - Invitation data (email, role, optional message)
   * @returns Created invitation
   */
  inviteToOrganization: async (
    orgId: number,
    data: InviteToOrgRequest
  ): Promise<Invitation> => {
    const response = await api.post(
      `/api/organizations/${orgId}/invitations`,
      data
    );
    return response.data.data.invitation;
  },

  /**
   * List all invitations for an organization
   *
   * GET /api/organizations/:orgId/invitations
   *
   * @param orgId - Organization ID
   * @param params - Optional query parameters (status filter)
   * @returns Array of invitations
   */
  getOrganizationInvitations: async (
    orgId: number,
    params?: ListInvitationsParams
  ): Promise<Invitation[]> => {
    const response = await api.get(`/api/organizations/${orgId}/invitations`, {
      params,
    });
    return response.data.data.invitations;
  },

  // ==========================================================================
  // Team Invitations
  // ==========================================================================

  /**
   * Invite a user to a team
   *
   * POST /api/teams/:teamId/invitations
   *
   * @param teamId - Team ID
   * @param data - Invitation data (email, role, optional message)
   * @returns Created invitation
   */
  inviteToTeam: async (
    teamId: number,
    data: InviteToBrandSpaceRequest
  ): Promise<Invitation> => {
    const response = await api.post(
      `/api/teams/${teamId}/invitations`,
      data
    );
    return response.data.data.invitation;
  },

  /**
   * List all invitations for a team
   *
   * GET /api/teams/:teamId/invitations
   *
   * @param teamId - Team ID
   * @param params - Optional query parameters (status filter)
   * @returns Array of invitations
   */
  getTeamInvitations: async (
    teamId: number,
    params?: ListInvitationsParams
  ): Promise<Invitation[]> => {
    const response = await api.get(
      `/api/teams/${teamId}/invitations`,
      { params }
    );
    return response.data.data.invitations;
  },

  // ==========================================================================
  // Invitation Management
  // ==========================================================================

  /**
   * Cancel an invitation
   *
   * DELETE /api/invitations/:id
   *
   * @param invitationId - Invitation ID to cancel
   * @returns Success message
   */
  cancelInvitation: async (invitationId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/invitations/${invitationId}`);
    return response.data;
  },

  /**
   * Resend an invitation email
   *
   * POST /api/invitations/:id/resend
   *
   * @param invitationId - Invitation ID to resend
   * @returns Updated invitation
   */
  resendInvitation: async (invitationId: number): Promise<Invitation> => {
    const response = await api.post(`/api/invitations/${invitationId}/resend`);
    return response.data.data.invitation;
  },

  // ==========================================================================
  // Public Invitation Routes (Token-based)
  // ==========================================================================

  /**
   * Verify an invitation token (public - no auth required)
   *
   * GET /api/invitations/verify/:token
   *
   * Returns invitation details including organization/brand space info
   *
   * @param token - Invitation token (64 hex characters)
   * @returns Invitation with related entity details
   */
  verifyToken: async (token: string): Promise<VerifyInvitationResponse> => {
    const response = await api.get(`/api/invitations/verify/${token}`);
    return response.data.data;
  },

  /**
   * Accept an invitation
   *
   * POST /api/invitations/:token/accept
   *
   * Requires authentication. User email must match invitation email.
   *
   * @param token - Invitation token
   * @returns Organization or brand space details
   */
  acceptInvitation: async (token: string): Promise<AcceptInvitationResponse> => {
    const response = await api.post(`/api/invitations/${token}/accept`);
    return response.data.data || {};
  },

  /**
   * Decline an invitation (public - no auth required)
   *
   * POST /api/invitations/:token/decline
   *
   * @param token - Invitation token
   * @returns Success message
   */
  declineInvitation: async (token: string): Promise<{ message: string }> => {
    const response = await api.post(`/api/invitations/${token}/decline`);
    return response.data;
  },
};

export default invitationsService;
